/**
 * Shape presets and stroke/fill option lists for the shape tool.
 * The canvas shape panel and the Fabric shape-creation code both read from here.
 * One mental model: everything is a plain data list (shapes, stroke styles,
 * categories) plus the small types that describe their entries - no logic lives
 * in this file, just the option data the UI and canvas draw from.
 */

import type { ShapePreset } from '@/types/layers/shape';

export const SHAPE_TYPES = {
  PATH: 'path',
  SVG: 'svg'
} as const;

export const SHAPE_PROPS = {
  FILL: 'fill',
  STROKE: 'stroke',
  STROKE_WIDTH: 'strokeWidth',
  STROKE_DASH_ARRAY: 'strokeDashArray',
  STROKE_LINE_JOIN: 'strokeLineJoin',
  STROKE_LINE_CAP: 'strokeLineCap',
  STROKE_MITER_LIMIT: 'strokeMiterLimit',
  STROKE_DASH_OFFSET: 'strokeDashOffset',
  BORDER_RADIUS: 'borderRadius',
  PATH: 'path',
  IS_ZERO_HEIGHT: 'isZeroHeight',
  ORIGINAL_PATH: 'originalPath',
  FILL_GRADIENT: 'fillGradient',
  STROKE_GRADIENT: 'strokeGradient'
} as const;

export const DEFAULT_SHAPE_COLOR = '#757575';

/** Stroke style presets matching the toolbar icons (row 1 of the stroke panel) */
export const STROKE_STYLE_PRESETS = [
  { name: 'none', label: 'None', dashArray: null },
  { name: 'solid', label: 'Solid', dashArray: '' },
  { name: 'dashed', label: 'Dashed', dashArray: '10,5' },
  { name: 'dotted', label: 'Dotted', dashArray: '4,4' },
  { name: 'fineDot', label: 'Fine Dot', dashArray: '2,4' }
] as const;

/** Stroke line-join options (row 2 of the stroke panel) */
export const STROKE_LINE_JOIN_OPTIONS = ['miter', 'round', 'bevel'] as const;

export const SHAPE_CATEGORIES = {
  BASIC: 'basic',
  POLYGONS: 'polygons',
  LINES: 'lines'
} as const;

export const DEFAULT_SHAPES: ShapePreset[] = [
  // Basic Shapes
  {
    name: 'Rectangle',
    path: 'M 0 0 L 200 0 L 200 150 L 0 150 Z',
    viewBox: '0 0 200 150',
    category: SHAPE_CATEGORIES.BASIC
  },
  {
    name: 'Square',
    path: 'M 0 0 L 150 0 L 150 150 L 0 150 Z',
    viewBox: '0 0 150 150',
    category: SHAPE_CATEGORIES.BASIC
  },
  {
    name: 'Circle',
    path: 'M 100 0 A 100 100 0 1 1 100 200 A 100 100 0 1 1 100 0 Z',
    viewBox: '0 0 200 200',
    category: SHAPE_CATEGORIES.BASIC
  },
  {
    name: 'Ellipse',
    path: 'M 125 0 A 125 75 0 1 1 125 150 A 125 75 0 1 1 125 0 Z',
    viewBox: '0 0 250 150',
    category: SHAPE_CATEGORIES.BASIC
  },
  {
    name: 'Triangle',
    path: 'M 100 0 L 200 175 L 0 175 Z',
    viewBox: '0 0 200 175',
    category: SHAPE_CATEGORIES.BASIC
  },
  {
    name: 'Right Triangle',
    path: 'M 0 0 L 200 175 L 0 175 Z',
    viewBox: '0 0 200 175',
    category: SHAPE_CATEGORIES.BASIC
  },

  // Polygons
  {
    name: 'Diamond',
    path: 'M 100 0 L 200 100 L 100 200 L 0 100 Z',
    viewBox: '0 0 200 200',
    category: SHAPE_CATEGORIES.POLYGONS
  },
  {
    name: 'Pentagon',
    path: 'M 100 0 L 195 69 L 159 181 L 41 181 L 5 69 Z',
    viewBox: '0 0 200 181',
    category: SHAPE_CATEGORIES.POLYGONS
  },
  {
    name: 'Hexagon',
    path: 'M 50 0 L 150 0 L 200 87 L 150 173 L 50 173 L 0 87 Z',
    viewBox: '0 0 200 173',
    category: SHAPE_CATEGORIES.POLYGONS
  },
  {
    name: 'Heptagon',
    path: 'M 100 0 L 178 35 L 197 119 L 143 190 L 57 190 L 3 119 L 22 35 Z',
    viewBox: '0 0 200 200',
    category: SHAPE_CATEGORIES.POLYGONS
  },
  {
    name: 'Octagon',
    path: 'M 59 0 L 141 0 L 200 59 L 200 141 L 141 200 L 59 200 L 0 141 L 0 59 Z',
    viewBox: '0 0 200 200',
    category: SHAPE_CATEGORIES.POLYGONS
  },

  // Lines
  {
    name: 'Solid Line',
    path: 'M 20 50 L 180 50',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    isZeroHeight: true
  },
  {
    name: 'Dashed Line',
    path: 'M 20 50 L 180 50',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    strokeDashArray: '10,5',
    isZeroHeight: true
  },
  {
    name: 'Dotted Line',
    path: 'M 20 50 L 180 50',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    strokeDashArray: '2,4',
    isZeroHeight: true
  },
  {
    name: 'Dashed Arrow',
    path: 'M 20 50 L 170 50 M 155 35 L 170 50 L 155 65',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    strokeDashArray: '10,5'
  },
  {
    name: 'Solid Arrow',
    path: 'M 20 50 L 170 50 M 155 35 L 170 50 L 155 65',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES
  },
  {
    name: 'Arrow Start',
    path: 'M 45 50 L 180 50 M 45 50 L 60 35 M 45 50 L 60 65',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    strokeDashArray: '10,5'
  },
  {
    name: 'Circle Line',
    path: 'M 20 50 L 165 50 M 165 50 A 5 5 0 1 1 175 50 A 5 5 0 1 1 165 50',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    strokeDashArray: '10,5'
  },
  {
    name: 'Diamond Line',
    path: 'M 20 50 L 160 50 M 160 50 L 170 40 L 180 50 L 170 60 Z',
    viewBox: '0 0 200 100',
    category: SHAPE_CATEGORIES.LINES,
    strokeDashArray: '10,5'
  }
];
